import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { Archive } from "@mui/icons-material";
import { archiveTrainingGroup } from "./api";
import { getTrainingGroupId } from "./types";
import type { TrainingGroup } from "./types";
import DialogHeader from "./DialogHeader";

interface ArchiveGroupDialogProps {
  target: { group: TrainingGroup } | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const ArchiveGroupDialog: React.FC<ArchiveGroupDialogProps> = ({
  target,
  onClose,
  onSuccess,
  onError,
}) => {
  const [archiving, setArchiving] = useState(false);

  const handleArchive = async () => {
    if (!target) return;
    const trainingGroupId = getTrainingGroupId(target.group);
    if (trainingGroupId == null) return;
    try {
      setArchiving(true);
      await archiveTrainingGroup(trainingGroupId);
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error archiving training group:", err);
      onError("Failed to archive training group. Please try again.");
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Dialog
      open={target != null}
      onClose={() => !archiving && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Archive Group"
        icon={<Archive color="warning" />}
        onClose={onClose}
        closeDisabled={archiving}
      />
      <DialogContent dividers>
        <Typography variant="body1">
          Are you sure you want to archive{" "}
          <strong>{target?.group.group_name}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Archived groups will no longer be visible to members. This action
          cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={archiving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={archiving ? <CircularProgress size={18} /> : <Archive />}
          onClick={handleArchive}
          disabled={archiving}
        >
          {archiving ? "Archiving…" : "Archive Group"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArchiveGroupDialog;
